import React, { useEffect, useState } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { ListCommanWay, PostFormData } from "API/commonApi";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  setAiReponse: any;
  setAiLoading: any;
  aiLoading: boolean;
}

const MultiImageUploadModal: React.FC<Props> = ({ isOpen, onClose, setAiReponse, setAiLoading, aiLoading }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const [showCamera, setShowCamera] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

  const getCameras = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((device) => device.kind === "videoinput");
    setVideoDevices(videoInputs);
  };

  const openWebcam = async (deviceId?: string) => {
    try {
      const constraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
      setVideoStream(stream);
      setShowCamera(true);
    } catch (err) {
      alert("Unable to access the camera");
      console.error(err);
    }
  };

  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  // Switch between available cameras
  const switchCamera = async () => {
    if (videoDevices.length <= 1) return;

    const nextIndex = (currentDeviceIndex + 1) % videoDevices.length;
    setCurrentDeviceIndex(nextIndex);
    await openWebcam(videoDevices[nextIndex].deviceId);
  };
  // Initialize cameras on component mount
  useEffect(() => {
    getCameras();
  }, []);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: "image/jpeg" });
      const url = URL.createObjectURL(file);
      setFiles((prev) => [...prev, file]);
      setPreviewUrls((prev) => [...prev, url]);
      stopCamera();
    }, "image/jpeg");
  };

  const stopCamera = () => {
    videoStream?.getTracks().forEach((track) => track.stop());
    setShowCamera(false);
    setVideoStream(null);
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);
    const newPreviews = selected.map((file) => URL.createObjectURL(file));

    setFiles((prev) => [...prev, ...selected]);
    setPreviewUrls((prev) => [...prev, ...newPreviews]);
  };

  const handleRemove = (index: number) => {
    const updatedFiles = [...files];
    const updatedPreviews = [...previewUrls];
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    setFiles(updatedFiles);
    setPreviewUrls(updatedPreviews);
  };

  const { mutate }: any = useMutation((data) => PostFormData("/admin/ai/parcel-detect", data), {
    onSuccess: (data: any) => {
      if (data.status === "SUCCESS") {
        console.log(data.data);
        setAiReponse(data?.data || []);
      } else {
        alert("Cannot Load Data");
        setAiLoading(false);
      }
    },
    onError: (data: any) => {
      alert("Cannot Load Data");
      setAiLoading(false);
      //typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleSave = async () => {
    if (aiLoading) {
      return;
    }
    setAiLoading(true);
    if (files.length === 0) return alert("No files selected!");
    onClose();
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
   
      {showCamera && (
        <div className="absolute z-10 top-0 left-0 w-full h-full bg-black-900/70 flex justify-center items-center">
          <div className="mb-4">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-[80vh] bg-black rounded" />
            <div className="flex gap-2 justify-end mt-2">
              <button onClick={switchCamera} className="bg-blue-500 text-white px-4 py-2 rounded">
                Switch Camera
              </button>
              <button onClick={capturePhoto} className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700">
                Capture
              </button>
              <button onClick={stopCamera} className="bg-red-600 text-white px-4 py-1 rounded hover:bg-red-700">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upload Photos</h2>
          <button
            onClick={() => {
              openWebcam();
            }}
            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition text-sm"
          >
            Open Camera
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {previewUrls.map((url, idx) => (
            <div key={idx} className="relative group">
              <img src={url} alt={`form-${idx}`} className="w-full h-28 object-cover rounded border" />
              <button
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs hover:bg-red-700"
                onClick={() => handleRemove(idx)}
              >
                🗑️
              </button>
            </div>
          ))}

          <label className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded w-full h-28 text-3xl text-gray-400 cursor-pointer hover:bg-gray-100 transition">
            +
            <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={handleAddFiles} />
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition">
            Save
          </button>
          <button onClick={onClose} className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiImageUploadModal;
